'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, XAxis, YAxis, Rectangle } from 'recharts';

import { ChartContainer as ShadcnChartContainer, ChartTooltip } from '@/components/ui/chart';
import { formatMoney } from "@/lib/money";
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

// Custom bar shape that applies radius only to the topmost segment with a non-zero value
function createStackedBarShape(processors: string[]) {
    return function StackedBarShape(props: any) {
        const { x, y, width, height, fill, payload, dataKey } = props;

        // Find the topmost processor with a non-zero value for this data point
        let topmostProcessor: string | null = null;
        for (let i = processors.length - 1; i >= 0; i--) {
            const proc = processors[i];
            if (payload[proc] && payload[proc] > 0) {
                topmostProcessor = proc;
                break;
            }
        }

        // Apply radius only if this bar is the topmost one
        const isTopmost = dataKey === topmostProcessor;
        const radius: [number, number, number, number] = isTopmost ? [4, 4, 0, 0] : [0, 0, 0, 0];

        return <Rectangle x={x} y={y} width={width} height={height} fill={fill} radius={radius} />;
    };
}

type RevenueByProcessorData = { processorName: string; amount: number; count: number };
type RevenueByProcessorTimeSeriesEntry = { [processorName: string]: { amount: number; count: number } };

export type RevenueByProcessorViewMode = 'timeSeries' | 'aggregated';

interface RevenueByProcessorChartProps {
    data: RevenueByProcessorData[] | undefined;
    timeSeriesData?: { data: Record<string, RevenueByProcessorTimeSeriesEntry>; processors: string[] };
    currency: string;
    className?: string;
    viewMode?: RevenueByProcessorViewMode;
    granularity?: 'daily' | 'weekly' | 'monthly';
}

// Helper to get week start date (Monday)
function getWeekStart(date: Date): string {
    const d = new Date(date);
    const day = d.getUTCDay();
    const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
    d.setUTCDate(diff);
    return d.toISOString().split('T')[0];
}

// Helper to get month start date
function getMonthStart(date: Date): string {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

const CHART_COLORS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
];

function getProcessorColor(index: number): string {
    return CHART_COLORS[index % CHART_COLORS.length];
}

export function RevenueByProcessorChart({ data, timeSeriesData, currency, className = '', viewMode = 'timeSeries', granularity = 'daily' }: RevenueByProcessorChartProps) {
    const { t } = useTranslation('analytics');
    const aggregatedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map(item => ({
            name: item.processorName,
            label: item.processorName.length > 20 ? `${item.processorName.slice(0, 20).trim()}…` : item.processorName,
            value: item.amount,
            count: item.count
        }));
    }, [data]);

    const timeSeriesChartData = React.useMemo(() => {
        if (!timeSeriesData || !timeSeriesData.data) return [];

        // First, get the raw daily data
        const rawData = Object.entries(timeSeriesData.data)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, processors]) => {
                const entry: any = { date };
                Object.entries(processors).forEach(([processor, data]) => {
                    entry[processor] = data.amount;
                    entry[`${processor}_count`] = data.count;
                });
                return entry;
            });

        // If daily, return as-is
        if (granularity === 'daily') return rawData;

        // Group by granularity
        const grouped: Record<string, any[]> = {};
        rawData.forEach(entry => {
            const d = new Date(entry.date);
            const key = granularity === 'weekly' ? getWeekStart(d) : getMonthStart(d);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(entry);
        });

        // Aggregate grouped data
        const processors = timeSeriesData.processors || [];
        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, entries]) => {
                const aggregated: any = { date };
                processors.forEach(processor => {
                    aggregated[processor] = entries.reduce((sum, e) => sum + (e[processor] || 0), 0);
                    aggregated[`${processor}_count`] = entries.reduce((sum, e) => sum + (e[`${processor}_count`] || 0), 0);
                });
                return aggregated;
            });
    }, [timeSeriesData, granularity]);

    const processors = timeSeriesData?.processors || [];

    const formatAxisValue = (value: number) => {
        const majorUnits = value / 100;
        if (majorUnits >= 1000000) return `${(majorUnits / 1000000).toFixed(1)}M`;
        if (majorUnits >= 1000) return `${(majorUnits / 1000).toFixed(0)}K`;
        return `${majorUnits.toFixed(0)}`;
    };

    const labelFormatter = (label: string) => {
        try {
            return formatBucketDate(label, { month: 'short', day: 'numeric' });
        } catch {
            return label;
        }
    };

    const AggregatedCustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0].payload;
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{item.name}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.revenue')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{formatMoney(item.value, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.transactions')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{item.count.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        );
    };

    const TimeSeriesCustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
        const reversed = [...payload].reverse();
        const dataItem = payload[0]?.payload;

        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{labelFormatter(String(label))}</p>
                <div className="space-y-1">
                    {reversed.map((entry: any, idx: number) => {
                        const value = Number(entry.value) || 0;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                        const countKey = `${entry.dataKey}_count`;
                        const count = dataItem?.[countKey] || 0;

                        return (
                            <div key={idx} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                                    <span className="text-sm text-muted-foreground">{entry.name}:</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-medium text-card-foreground">{formatMoney(value, currency)}</span>
                                    <span className="text-xs text-muted-foreground ml-2">({percentage}%)</span>
                                    {count > 0 && (
                                        <span className="text-xs text-muted-foreground ml-1">• {count}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div className="pt-2 mt-2 border-t border-border">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-medium text-muted-foreground">{t('charts.total')}:</span>
                            <span className="text-sm font-semibold text-card-foreground">{formatMoney(total, currency)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Time series view
    if (viewMode === 'timeSeries') {
        if (!timeSeriesChartData.length) {
            return <EmptyState message={t('charts.noProcessorData')} className={className} />;
        }

        const config: Record<string, { label: string; color: string }> = {};
        processors.forEach((processor, index) => {
            config[processor] = { label: processor, color: getProcessorColor(index) };
        });

        return (
            <div className={`h-full ${className}`}>
                <ShadcnChartContainer config={config} className="w-full h-full">
                    <BarChart accessibilityLayer data={timeSeriesChartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={labelFormatter} />
                        <YAxis tickFormatter={formatAxisValue} />
                        <ChartTooltip cursor={false} content={<TimeSeriesCustomTooltip />} position={{ y: 220 }} />
                        {processors.map((processor, index) => (
                            <Bar
                                key={processor}
                                dataKey={processor}
                                name={processor}
                                stackId="processors"
                                fill={getProcessorColor(index)}
                                shape={createStackedBarShape(processors)}
                            />
                        ))}
                    </BarChart>
                </ShadcnChartContainer>
            </div>
        );
    }

    // Aggregated view (horizontal bar chart with different colors)
    if (!aggregatedData.length) {
        return <EmptyState message={t('charts.noProcessorData')} className={className} />;
    }

    const chartHeight = Math.max(256, aggregatedData.length * 40);

    const config: Record<string, { label: string; color: string }> = {};
    aggregatedData.forEach((item, index) => {
        config[item.name] = { label: item.name, color: getProcessorColor(index) };
    });

    return (
        <div className={`w-full ${className}`} style={{ height: chartHeight }}>
            <ShadcnChartContainer config={config} className="w-full h-full">
                <BarChart
                    data={aggregatedData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                >
                    <XAxis type="number" tickFormatter={formatAxisValue} />
                    <YAxis
                        type="category"
                        dataKey="label"
                        width={180}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <ChartTooltip cursor={false} content={<AggregatedCustomTooltip />} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {aggregatedData.map((entry, index) => (
                            <Cell key={entry.name} fill={getProcessorColor(index)} />
                        ))}
                    </Bar>
                </BarChart>
            </ShadcnChartContainer>
        </div>
    );
}
